import React, { useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  ImageListItem,
  ImageListItemBar,
  MenuItem,
  Modal,
  Stack,
  Switch,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import {
  ColorField,
  Label,
  UploadComponent,
} from "app/pages/presentation/module/form/types";
import * as Yup from "yup";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Galleries from "app/pages/media/galleries/Galleries";
import CompressorDeviceSelector from "app/pages/customers/dsok/smartkioskFields/CompressorDeviceSelector";
import { getGallery, uploadToImagekit } from "app/services/api/media";
import DeleteIcon from "@mui/icons-material/Delete";
import mapValues from "lodash/mapValues";
import i18n from "app/config/i18n";
import Images from "app/pages/media/images/Images";
import { PROD_URL } from "app/services/config";
import { sanitizeLogoUrlString } from "../utils/sanitizeLogoUrlString";
import { useSnackbar } from "notistack";
import {
  DSOK_AUTH_METHODS,
  DSOK_LOYALTY_SYSTEMS,
  DSOK_PAYMENT_METHODS,
  DSOK_SECONDARY_AUTH_METHODS,
  DSOK_SLIDER_EFFECTS,
  EASY_SERVICES,
} from "app/utils/constants/settings";

const FIELD_NAME = "SETTINGS_FIELD";

const PRODUCT_IMAGE_PLACEHOLDERS = [
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-default.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.default",
    isDefault: true,
  },
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-burger.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.burger",
  },
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-food.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.food",
  },
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-coffee.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.coffee",
  },
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-soup.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.soup",
  },
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-icecream.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.icecream",
  },
  {
    url: "https://ik.imagekit.io/7s2whdfxn/Placeholders/placeholder-cake.png",
    labelKey: "pages.dsokDetails.appSettings.placeholders.cake",
  },
];

function ensureArrayForSelect(v) {
  return Array.isArray(v) ? v : v != null && v !== "" ? [v] : [];
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75vw",
  maxHeight: "90vh",
  overflow: "scroll",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

export const SETTINGS_FIELD_SCHEMA = {
  inactivityTimeout: "",
  orderSuccesPageTimeout: "",
  orderErrorPageTimeout: "",
  accessibilityButton: "",
  product: {
    displayOutOfStockProducts: "",
    displayAllergens: "",
    columns: "",
    ProductConfigModal: {
      columns: "",
    },
  },
  mainScreen: {
    slider: {
      duration: "",
      galleryUrl: {},
      effect: "",
    },
    title: {
      display: "",
      label: {},
    },
  },
  takeAway: "",
  dineIn: "",
  locator: "",
  showPagerImage: false,
  locatorType: "locator",
  pickupAtCounter: "",
  allowOrderNote: false,
  smsNotificationsEnabled: "",
  hideDeliveryTypeIcon: false,
  paymentButton: "",
  auth: "",
  secondaryAuth: "",
  printer: {
    printQrCode: false,
    cutPaperBeforePrintOrderDetails: false,
    printDailyOrderCounter: false,
    printOrderCode: false,
    printPaymentReceipt: false,
    printDiscountInfo: false,
    // Firma + meno prihláseného zamestnanca na doklade (nenastavené = zapnuté).
    printAuthenticatedUser: true,
  },
  accessSystem: {
    enabled: false,
    printTickets: false,
    printQrCode: false,
    sendSmsEnabled: false,
    sendEmailEnabled: false,
  },
  loyaltySystem: {
    isEnabled: false,
    type: "datavision",
    vexion: {
      businessId: "",
      enableRegistration: false,
    },
  },
  smartkiosk: {
    productSettings: {
      grid: {
        showAddToCartAction: "",
        showPrice: "",
        showPriceForAuthenticatedUser: true,
      },
      productVariants: {
        showTitle: "",
        showDescription: "",
        enableQuantitySelection: "",
        maxQuantity: "",
      },
      recommendedProducts: {
        showTitle: "",
        viewType: "",
        columns: "",
      },
      detail: {
        showSku: "",
        showBrand: "",
      },
    },
    categoriesDisplaySettings: [],
    dynamicComponents: [],
    cartSettings: {
      maxItems: "",
      maxItemQuantity: "",
      showBottomMessage: "",
      authUser: {
        maxItems: "",
        maxItemQuantity: "",
        showBottomMessage: "",
      },
    },
  },
};

export const getSettingsFieldValidationSchema = (t) => ({
  inactivityTimeout: Yup.number().required(t("messages.checkRequiredFields")),
  orderSuccesPageTimeout: Yup.number().required(
    t("messages.checkRequiredFields"),
  ),
  orderErrorPageTimeout: Yup.number().required(
    t("messages.checkRequiredFields"),
  ),
  product: Yup.object().shape({
    columns: Yup.number().required(t("messages.checkRequiredFields")),
    ProductConfigModal: Yup.object().shape({
      columns: Yup.number().required(t("messages.checkRequiredFields")),
    }),
  }),
  mainScreen: Yup.object().shape({
    slider: Yup.object().shape({
      duration: Yup.number().required(t("messages.checkRequiredFields")),
      galleryUrl: Yup.lazy((lazyObj) =>
        Yup.object(
          mapValues(lazyObj, () =>
            Yup.string().required(t("messages.checkRequiredFields")),
          ),
        ),
      ),
    }),
    title: Yup.object().shape({
      label: Yup.lazy((lazyObj) =>
        Yup.object(
          mapValues(lazyObj, () =>
            Yup.string().required(t("messages.checkRequiredFields")),
          ),
        ),
      ),
    }),
  }),
});

const SmartkioskSettingsFields = ({
  handleChange,
  expanded,
  isAdmin,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [openSelector, setOpenSelector] = useState();
  const [galleryTitle, setGalleryTitle] = useState({});
  const [galleryImage, setGalleryImage] = useState({});
  const [activeTab, setActiveTab] = useState("basic");

  // Migration: if new master key is unset, inherit from old UI setting.
  useEffect(() => {
    const next = values?.settings?.accessSystem?.enabled;
    const prev = values?.customer?.customerUISettings?.enableAccessSystem;
    if (next === undefined && prev !== undefined) {
      setFieldValue("settings.accessSystem.enabled", prev === true);
    }
  }, [
    setFieldValue,
    values?.customer?.customerUISettings?.enableAccessSystem,
    values?.settings?.accessSystem?.enabled,
  ]);

  useEffect(() => {
    values.customer.languages &&
      setSelectedLanguage(values.customer.languages[0]);
  }, [values.customer.languages]);

  const updateGalleries = useCallback(async () => {
    const newGalleryImage = {};
    const newGalleryTitle = {};
    const galleryUrlObj = values?.settings.mainScreen?.slider?.galleryUrl || {};
    for (const gallery of Object.keys(galleryUrlObj)) {
      const galleryUrl = galleryUrlObj[gallery];
      if (!galleryUrl) continue;
      await getGallery(galleryUrl).then((response) => {
        newGalleryTitle[gallery] = response?.label || "";
        const firstImage =
          (response?.images && response?.images[0]) || undefined;
        const imageUrl =
          (firstImage &&
            (firstImage.path || firstImage.url || firstImage.imageUrl)) ||
          "";
        newGalleryImage[gallery] = imageUrl;
      });
    }
    setGalleryImage((prev) => ({ ...prev, ...newGalleryImage }));
    setGalleryTitle((prev) => ({
      ...prev,
      ...newGalleryTitle,
    }));
  }, [values?.settings.mainScreen?.slider?.galleryUrl]);

  useEffect(() => {
    // Only update galleries if the component is actually expanded/visible
    if (
      expanded === FIELD_NAME &&
      values?.settings.mainScreen?.slider?.galleryUrl
    ) {
      updateGalleries();
    } else if (!expanded || expanded !== FIELD_NAME) {
      setGalleryImage({});
    }
  }, [values.settings.mainScreen, updateGalleries, expanded]);

  const handleGallerySelect = (galleryItem, language) => {
    setOpenSelector();
    setFieldValue(
      `settings.mainScreen.slider.galleryUrl.${language}`,
      galleryItem.url,
    );
    // Optimistically set title from selected item
    if (galleryItem?.label) {
      setGalleryTitle((prev) => ({ ...prev, [language]: galleryItem.label }));
    }
    // Optimistically load and display the first image from the selected gallery
    if (galleryItem?.url) {
      getGallery(galleryItem.url).then((response) => {
        const firstImage =
          (response?.images && response?.images[0]) || undefined;
        const imageUrl =
          (firstImage &&
            (firstImage.path || firstImage.url || firstImage.imageUrl)) ||
          "";
        setGalleryImage((prev) => ({ ...prev, [language]: imageUrl }));
        setGalleryTitle((prev) => ({
          ...prev,
          [language]: response?.label || prev?.[language] || "",
        }));
      });
    }
  };

  const PlaceholderPicker = ({ name, value }) => {
    return (
      <Box>
        <Grid container spacing={1}>
          {PRODUCT_IMAGE_PLACEHOLDERS.map((placeholder) => {
            const isSelected = value === placeholder.url;
            return (
              <Grid item xs={6} key={placeholder.url}>
                <Box
                  onClick={() =>
                    setFieldValue(name, isSelected ? "" : placeholder.url)
                  }
                  sx={{
                    position: "relative",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.15s ease-in-out",
                    backgroundColor: isSelected
                      ? "primary.lighter"
                      : "background.paper",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={placeholder.url}
                    alt={t(placeholder.labelKey, placeholder.labelKey)}
                    sx={{
                      width: "100%",
                      height: 100,
                      objectFit: "contain",
                      display: "block",
                      p: 1,
                    }}
                  />
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: 13,
                          lineHeight: 1,
                          fontWeight: "bold",
                        }}
                      >
                        ✓
                      </Typography>
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    display="block"
                    textAlign="center"
                    sx={{ pb: 0.5, color: "text.secondary" }}
                  >
                    {t(placeholder.labelKey, placeholder.labelKey.split(".").pop())}
                    {placeholder.isDefault && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 0.5, color: "primary.main", fontWeight: 600 }}
                      >
                        ({t("pages.dsokDetails.appSettings.placeholders.defaultBadge", "predvolený")})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const ImagePicker = ({ name, value }) => {
    const [open, setOpen] = useState(false);
    const editedCustomerId = values?.customer?.customerId;
    const modalStyleLocal = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "75vw",
      maxHeight: "90vh",
      overflow: "scroll",
      bgcolor: "background.paper",
      boxShadow: 24,
      p: 4,
    };
    return (
      <>
        <Box
          onClick={() => {
            if (!editedCustomerId) {
              enqueueSnackbar(
                t(
                  "pages.dsokDetails.appSettings.imagePicker.missingCustomerId",
                  {
                    defaultValue:
                      "Cannot open image browser because customer ID is not available.",
                  },
                ),
                { variant: "error" },
              );
              return;
            }
            setOpen(true);
          }}
          sx={{
            width: "100%",
            minHeight: "160px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 1,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "action.hover",
            },
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          {value ? (
            <Box sx={{ position: "relative", width: "100%" }}>
              <Avatar
                src={typeof value === "string" ? value : ""}
                variant="rounded"
                sx={{
                  height: "auto",
                  width: "100%",
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("pages.dsokPromotions.selectImage", {
                defaultValue: "Click to select image",
              })}
            </Typography>
          )}
        </Box>
        <Modal open={open} onClose={() => setOpen(false)}>
          <Div sx={modalStyleLocal}>
            <Images
              layout={"row-reverse"}
              selectorMode
              allowed={["jpg", "jpeg", "png", "svg"]}
              customerIdOverride={editedCustomerId}
              onCancel={() => setOpen(false)}
              onSelect={(image) => {
                if (!editedCustomerId) {
                  enqueueSnackbar(
                    t(
                      "pages.dsokDetails.appSettings.imagePicker.missingCustomerId",
                      {
                        defaultValue:
                          "Cannot select image because customer ID is not available.",
                      },
                    ),
                    { variant: "error" },
                  );
                  setOpen(false);
                  return;
                }
                const imageUrl = sanitizeLogoUrlString(
                  `${PROD_URL}/uploads/files/${editedCustomerId}/Images/${image.filename}`,
                );
                setFieldValue(name, imageUrl);
                setOpen(false);
              }}
            />
          </Div>
        </Modal>
      </>
    );
  };

  const Selector = ({ label, help, title, image, setOpenModal, name }) => {
    return (
      <Div sx={{ mt: 0, mb: 2.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            margin: "0 0 0 10px",
            background: "white",
            color: "text.secondary",
            width: "fit-content",
          }}
        >
          {label}
        </Typography>
        <ImageListItem
          sx={{
            "& .MuiImageListItemBar-root": {
              transition: "visibility 0s, opacity 0.3s linear",
              opacity: 0,
            },

            "&:hover .MuiImageListItemBar-root": {
              visibility: "visible",
              opacity: 1,
            },
            width: "fit-content",
            height: "auto",
          }}
        >
          <Avatar
            src={image || "/images/pages/not_found.svg"}
            variant={"rounded"}
            sx={{
              height: "auto",
              width: `100%`,
              maxWidth: "250px",
              objectFit: "contain",
              cursor: "pointer",
            }}
            onClick={() => setOpenModal(true)}
          />
          {image && (
            <ImageListItemBar
              subtitle={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mb: 0,
                  }}
                >
                  <Typography variant="body">{title}</Typography>
                  <DeleteIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => setFieldValue(name, "")}
                  />
                </Stack>
              }
            />
          )}
        </ImageListItem>
      </Div>
    );
  };

  const content = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <TabContext value={activeTab}>
                <Div sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList onChange={(_, newTab) => setActiveTab(newTab)}>
                    <Tab
                      label={t("pages.dsokDetails.appSettings.tabs.basic")}
                      value={"basic"}
                    />
                    <Tab
                      label={t("pages.dsokDetails.appSettings.tabs.mainScreen")}
                      value={"mainScreen"}
                    />
                    <Tab
                      label={t("pages.dsokDetails.appSettings.tabs.images")}
                      value={"images"}
                    />
                    <Tab
                      label={t("pages.dsokDetails.appSettings.tabs.slider")}
                      value={"slider"}
                    />
                    <Tab
                      label={t(
                        "pages.dsokDetails.appSettings.tabs.authSettings.title",
                      )}
                      value={"auth"}
                    />
                    <Tab
                      label={t(
                        "pages.dsokDetails.appSettings.tabs.printer.title",
                      )}
                      value={"printer"}
                    />
                    <Tab
                      label={t(
                        "pages.dsokDetails.appSettings.tabs.accessSystem.title",
                      )}
                      value={"accessSystem"}
                    />
                    <Tab
                      label={t(
                        "pages.dsokDetails.appSettings.tabs.paymentMethods.title",
                      )}
                      value={"paymentMethods"}
                    />
                    <Tab
                      label={t(
                        "pages.dsokDetails.appSettings.tabs.loyalty.title",
                      )}
                      value={"loyalty"}
                    />
                    {values?.customer?.lineOfBusiness === "concreteStation" && (
                      <Tab
                        label={t(
                          "pages.dsokDetails.appSettings.tabs.concreteStation.title",
                          { defaultValue: "Nastavenie betonárne" },
                        )}
                        value={"concreteStation"}
                      />
                    )}
                  </TabList>
                </Div>

                <TabPanel value={"basic"} sx={{ p: 0, m: 0 }}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    container
                    spacing={2}
                    sx={{ p: 0, m: 0 }}
                  >
                    {values.customer?.frontendType !== "easyFood" && (
                      <>
                        <Grid item xs={12}>
                          <Alert
                            severity="info"
                            variant="outlined"
                            sx={{ mt: 1, mb: 1 }}
                          >
                            <AlertTitle>
                              {t("pages.dsokDetails.easyServicesHelp.title")}
                            </AlertTitle>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ display: "block", mb: 1 }}
                            >
                              {t(
                                "pages.dsokDetails.easyServicesHelp.description",
                              )}
                            </Typography>
                            <Box
                              component="ul"
                              sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}
                            >
                              <li>
                                <Typography variant="body2">
                                  {t(
                                    "pages.dsokDetails.easyServicesHelp.optionEasyBuy",
                                  )}
                                </Typography>
                              </li>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ mt: 1, fontWeight: 500 }}
                            >
                              {t("pages.dsokDetails.easyServicesHelp.warning")}
                            </Typography>
                          </Alert>
                        </Grid>
                        <Grid item xs={12} md={4} lg={2}>
                          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                            <Field name="settings.easyServices">
                              {({ field, meta }) => {
                                const value = ensureArrayForSelect(field.value);
                                return (
                                  <>
                                    <TextField
                                      select
                                      fullWidth
                                      required
                                      InputLabelProps={{ required: false }}
                                      {...field}
                                      value={value}
                                      onChange={(e) =>
                                        field.onChange({
                                          target: {
                                            name: field.name,
                                            value: e.target.value,
                                          },
                                        })
                                      }
                                      label={t(
                                        "pages.dsokDetails.easyServices",
                                      )}
                                      SelectProps={{ multiple: true }}
                                      error={!!meta.error}
                                      helperText={meta.error}
                                    >
                                      {EASY_SERVICES.map((language, index) => (
                                        <MenuItem
                                          key={index}
                                          value={language.code}
                                        >
                                          {language.name}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    <Box sx={{ mt: 0.5 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {t(
                                          "pages.dsokDetails.easyServicesHelpText",
                                        )}
                                      </Typography>
                                    </Box>
                                  </>
                                );
                              }}
                            </Field>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <JumboTextField
                          type="number"
                          InputLabelProps={{ required: false }}
                          required
                          fullWidth
                          name="settings.inactivityTimeout"
                          label={
                            <Label
                              text={t("pages.dsokDetails.inactivityTimeout")}
                              helpText={t(
                                "pages.dsokDetails.inactivityTimeoutHelp",
                              )}
                              required
                            />
                          }
                          InputProps={{
                            value: values.settings?.inactivityTimeout,
                            onChange: (e) =>
                              setFieldValue(
                                "settings.inactivityTimeout",
                                e.target.value,
                              ),
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <JumboTextField
                          type="number"
                          InputLabelProps={{ required: false }}
                          required
                          fullWidth
                          name="settings.orderSuccesPageTimeout"
                          label={
                            <Label
                              text={t("pages.dsokDetails.orderSuccessTimeout")}
                              helpText={t(
                                "pages.dsokDetails.orderSuccessTimeoutHelp",
                              )}
                              required
                            />
                          }
                          InputProps={{
                            value: values.settings?.orderSuccesPageTimeout,
                            onChange: (e) =>
                              setFieldValue(
                                "settings.orderSuccesPageTimeout",
                                e.target.value,
                              ),
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <JumboTextField
                          type="number"
                          InputLabelProps={{ required: false }}
                          required
                          fullWidth
                          name="settings.orderErrorPageTimeout"
                          label={
                            <Label
                              text={t("pages.dsokDetails.orderErrorTimeout")}
                              helpText={t(
                                "pages.dsokDetails.orderErrorTimeoutHelp",
                              )}
                              required
                            />
                          }
                          InputProps={{
                            value: values.settings?.orderErrorPageTimeout,
                            onChange: (e) =>
                              setFieldValue(
                                "settings.orderErrorPageTimeout",
                                e.target.value,
                              ),
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="h6" component="h2">
                        {t("pages.dsokDetails.displaySettingsSectionTitle")}
                      </Typography>
                      <Divider sx={{ mt: 1, mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.takeAway"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.takeAway",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={t("pages.dsokDetails.takeAway")}
                          checked={Boolean(values.settings?.takeAway)}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.dineIn"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.dineIn",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={t("pages.dsokDetails.settings_dineIn_label")}
                          checked={Boolean(values.settings?.dineIn)}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.welcomeScreen.display"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.welcomeScreen.display",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t("pages.dsokDetails.showWelcomeScreen")}
                              helpText={t(
                                "pages.dsokDetails.showWelcomeScreenHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.welcomeScreen?.display}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.enableCallStaff"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.enableCallStaff",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t("pages.dsokDetails.enableCallStaff")}
                              helpText={t(
                                "pages.dsokDetails.enableCallStaffHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.enableCallStaff}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.enableStaffLogin"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.enableStaffLogin",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t("pages.dsokDetails.enableStaffLogin")}
                              helpText={t(
                                "pages.dsokDetails.enableStaffLoginHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.enableStaffLogin}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}></Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.enableCartRules"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.enableCartRules",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t("pages.dsokDetails.enableCartRules")}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.enableCartRules}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.enableCrossSell"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.enableCrossSell",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t("pages.dsokDetails.enableCrossSell")}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.enableCrossSell}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ mt: 2, mb: 0.5, mx: 0.5 }}
                      >
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.locator"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.locator",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={t("pages.dsokDetails.locator")}
                          checked={Boolean(values.settings?.locator)}
                        />
                        {values.settings?.locator && (
                          <FormControlLabel
                            sx={{ ml: 2 }}
                            control={
                              <Field
                                name="settings.showPagerImage"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.showPagerImage",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Label
                                text={t("pages.dsokDetails.showPagerImage", {
                                  defaultValue: "Zobraziť obrázok pageru",
                                })}
                                helpText={t(
                                  "pages.dsokDetails.showPagerImageHelp",
                                  {
                                    defaultValue:
                                      "Ak je zapnuté, pri výbere čísla pageru sa zobrazí obrázok pageru.",
                                  },
                                )}
                                sx={{ color: "text.primary" }}
                              />
                            }
                            checked={Boolean(values.settings?.showPagerImage)}
                          />
                        )}
                        {values.settings?.locator &&
                          values.settings?.showPagerImage && (
                            <FormControl sx={{ ml: 2, minWidth: 200 }}>
                              <JumboTextField
                                select
                                fullWidth
                                name="settings.locatorType"
                                SelectProps={{ multiple: false }}
                                label={
                                  <Label
                                    text={t("pages.dsokDetails.locatorType", {
                                      defaultValue: "Typ obrázku",
                                    })}
                                    helpText={t(
                                      "pages.dsokDetails.locatorTypeHelp",
                                      {
                                        defaultValue:
                                          "Podľa výberu sa zobrazí obrázok pageru alebo lokátora.",
                                      },
                                    )}
                                  />
                                }
                              >
                                <MenuItem value="locator">
                                  {t("pages.dsokDetails.locatorType_locator", {
                                    defaultValue: "Lokátor",
                                  })}
                                </MenuItem>
                                <MenuItem value="pager">
                                  {t("pages.dsokDetails.locatorType_pager", {
                                    defaultValue: "Pager",
                                  })}
                                </MenuItem>
                              </JumboTextField>
                            </FormControl>
                          )}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.pickupAtCounter"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.pickupAtCounter",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={t(
                            "pages.dsokDetails.settings_pickupAtCounter_label",
                          )}
                          checked={Boolean(values.settings?.pickupAtCounter)}
                        />
                      </Div>
                    </Grid>
                    {Boolean(values.settings?.pickupAtCounter) && (
                      <Grid item xs={12} md={4} lg={3}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.allowOrderNote"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.allowOrderNote",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={t(
                              "pages.dsokDetails.settings_allowOrderNote_label",
                              { defaultValue: "Povoliť poznámku k objednávke" },
                            )}
                            checked={Boolean(values.settings?.allowOrderNote)}
                          />
                        </Div>
                      </Grid>
                    )}
                    <Grid item xs={12} md={4} lg={3}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.smsNotificationsEnabled"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.smsNotificationsEnabled",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={t(
                            "pages.dsokDetails.settings_smsNotificationsEnabled_label",
                          )}
                          checked={Boolean(
                            values.settings?.smsNotificationsEnabled,
                          )}
                        />
                      </Div>
                    </Grid>
                    {values.customer?.frontendType === "easyFood" && (
                      <>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                          <Typography variant="h6" component="h2">
                            {t(
                              "pages.dsokDetails.easyFoodSettingsSectionTitle",
                            )}
                          </Typography>
                          <Divider sx={{ mt: 1, mb: 2 }} />
                        </Grid>
                        <Grid item xs={12} md={4} lg={3}>
                          <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Field
                                  name="settings.hideDeliveryTypeIcon"
                                  component={Switch}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "settings.hideDeliveryTypeIcon",
                                      e.target.checked,
                                    )
                                  }
                                />
                              }
                              label={t(
                                "pages.dsokDetails.hideDeliveryTypeIcon",
                              )}
                              checked={values.settings?.hideDeliveryTypeIcon}
                            />
                          </Div>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </TabPanel>
                <TabPanel value={"mainScreen"} sx={{ p: 0, m: 0 }}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    container
                    spacing={2}
                    sx={{ p: 0, m: 0 }}
                  >
                    <Grid item xs={12} md={12} lg={12} container spacing={2}>
                      <Grid item xs={12} md={4} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.mainScreen.title.display"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.mainScreen.title.display",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Label
                                text={t("pages.dsokDetails.displayTitle")}
                                helpText={t(
                                  "pages.dsokDetails.displayTitleHelp",
                                )}
                                sx={{ color: "text.primary" }}
                              />
                            }
                            checked={
                              values.settings?.mainScreen?.title?.display
                            }
                          />
                        </Div>
                      </Grid>
                      <Grid item xs={12} md={4} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.mainScreen.displayLogo"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.mainScreen.displayLogo",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Label
                                text={t("pages.dsokDetails.displayLogo")}
                                helpText={t(
                                  "pages.dsokDetails.displayLogoHelp",
                                )}
                                sx={{ color: "text.primary" }}
                              />
                            }
                            checked={values.settings?.mainScreen?.displayLogo}
                          />
                        </Div>
                      </Grid>
                      <Grid item xs={12} md={12} lg={12}></Grid>
                      <Grid item xs={12} md={4} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.mainScreen.icon.display"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.mainScreen.icon.display",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={t("pages.dsokDetails.iconDisplay")}
                            checked={values.settings?.mainScreen?.icon?.display}
                          />
                        </Div>
                      </Grid>
                      <Grid item xs={12} md={4} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.mainScreen.icon.move"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.mainScreen.icon.move",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={t("pages.dsokDetails.iconMove")}
                            checked={values.settings?.mainScreen?.icon?.move}
                          />
                        </Div>
                      </Grid>
                      <Grid item xs={12} md={4} lg={2}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <JumboTextField
                            type="number"
                            InputLabelProps={{ required: false }}
                            required
                            fullWidth
                            name="settings.mainScreen.icon.size"
                            label={
                              <Label
                                text={t("pages.dsokDetails.iconSize")}
                                helpText={t("pages.dsokDetails.iconSizeHelp")}
                                required
                              />
                            }
                            InputProps={{
                              value: values.settings?.mainScreen?.icon?.size,
                              onChange: (e) =>
                                setFieldValue(
                                  "settings.mainScreen.icon.size",
                                  e.target.value,
                                ),
                            }}
                          />
                        </Div>
                      </Grid>
                      <Grid item xs={12} md={4} lg={2}>
                        <Div sx={{ mt: 1.5, mb: 1, mx: 0.5 }}>
                          <ColorField
                            name="settings.mainScreen.icon.color"
                            lang={i18n.language}
                            parameters={{
                              label: {
                                [i18n.language]: t(
                                  "pages.dsokDetails.iconColor",
                                ),
                              },
                              required: false,
                            }}
                          />
                        </Div>
                      </Grid>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={"images"} sx={{ p: 0, m: 0 }}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    container
                    spacing={2}
                    sx={{ p: 0, m: 0 }}
                  >
                    <Grid item xs={12} md={12} lg={12} container spacing={2}>
                      <Grid item xs={12} md={2} lg={2}>
                        <Label text={t("pages.dsokDetails.mainLogo")} />
                        <ImagePicker
                          name="customer.mainLogo"
                          value={values.customer.mainLogo}
                        />
                      </Grid>
                      <Grid item xs={12} md={2} lg={2}>
                        <Label text={t("pages.dsokDetails.backgroundImage")} />
                        <ImagePicker
                          name="settings.backgroundImage"
                          value={values.settings.backgroundImage}
                        />
                      </Grid>
                      <Grid item xs={12} md={3} lg={3}>
                        <Label
                          text={t("pages.dsokDetails.appSettings.placeholders.title")}
                          helpText={t("pages.dsokDetails.appSettings.placeholders.help")}
                        />
                        <PlaceholderPicker
                          name="settings.productImagePlaceholder"
                          value={values.settings?.productImagePlaceholder || ""}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={"slider"} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl sx={{ mt: 1, mb: 1 }}>
                        <JumboTextField
                          type="number"
                          InputLabelProps={{ required: false }}
                          required
                          fullWidth
                          name="settings.mainScreen.slider.duration"
                          label={
                            <Label
                              text={t("pages.dsokDetails.slideChangeIn")}
                              helpText={t(
                                "pages.dsokDetails.slideChangeInHelp",
                              )}
                              required
                            />
                          }
                          InputProps={{
                            value:
                              values.settings?.mainScreen?.slider?.duration /
                              1000,
                            onChange: (e) =>
                              setFieldValue(
                                "settings.mainScreen.slider.duration",
                                e.target.value * 1000,
                              ),
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <JumboTextField
                          select
                          required
                          fullWidth
                          InputLabelProps={{ required: false }}
                          name="settings.mainScreen.slider.effect"
                          SelectProps={{
                            multiple: false,
                            defaultValue: "fade",
                          }}
                          label={
                            <Label text={t("pages.dsokDetails.sliderEffect")} />
                          }
                        >
                          {DSOK_SLIDER_EFFECTS.map((effect, index) => {
                            return (
                              <MenuItem key={index} value={effect.code}>
                                {effect.name}
                              </MenuItem>
                            );
                          })}
                        </JumboTextField>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}></Grid>
                    <Grid item xs={12} md={12} lg={4}>
                      <TabContext value={selectedLanguage}>
                        <Stack sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              borderBottom: 1,
                              borderColor: "divider",
                            }}
                          >
                            <TabList
                              onChange={(_, val) => setSelectedLanguage(val)}
                            >
                              {values.customer.languages?.map((lang) => {
                                return (
                                  <Tab
                                    key={lang}
                                    label={t(`languages.${lang}`)}
                                    value={lang}
                                  />
                                );
                              })}
                            </TabList>
                          </Box>
                          {values.customer.languages?.map((lang) => {
                            return (
                              <TabPanel
                                key={`panel_${lang}`}
                                value={lang}
                                sx={{ pb: 1, pr: 0, minHeight: 350, pl: 1 }}
                              >
                                <Div sx={{ mb: 1, mx: 0 }}>
                                  <JumboTextField
                                    fullWidth
                                    required
                                    InputLabelProps={{ required: false }}
                                    name={`settings.mainScreen.title.label.${lang}`}
                                    label={
                                      <Label
                                        text={t("pages.dsokDetails.title")}
                                        helpText={t(
                                          "pages.dsokDetails.titleHelp",
                                        )}
                                        required
                                      />
                                    }
                                  />
                                </Div>
                                <Selector
                                  label={
                                    <Label
                                      text={t(
                                        "pages.dsokDetails.sliderGallery",
                                      )}
                                      helpText={t(
                                        "pages.dsokDetails.sliderGalleryHelp",
                                      )}
                                      required
                                    />
                                  }
                                  image={galleryImage[lang]}
                                  title={galleryTitle[lang]}
                                  name={`settings.mainScreen.slider.galleryUrl.${lang}`}
                                  setOpenModal={() => setOpenSelector(lang)}
                                />
                              </TabPanel>
                            );
                          })}
                        </Stack>
                      </TabContext>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={"auth"} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    <Grid item xs={12} md={4} lg={4}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <Field name="customer.auth">
                          {({ field, meta }) => {
                            const value = ensureArrayForSelect(field.value);
                            return (
                              <TextField
                                select
                                fullWidth
                                InputLabelProps={{ required: false }}
                                {...field}
                                value={value}
                                onChange={(e) =>
                                  field.onChange({
                                    target: {
                                      name: field.name,
                                      value: e.target.value,
                                    },
                                  })
                                }
                                label={t(
                                  "pages.dsokDetails.appSettings.tabs.authSettings.loginMethods.title",
                                )}
                                SelectProps={{ multiple: true }}
                                error={!!meta.error}
                                helperText={meta.error}
                              >
                                {DSOK_AUTH_METHODS.map((authMethod, index) => (
                                  <MenuItem key={index} value={authMethod.code}>
                                    {t(
                                      `pages.dsokDetails.appSettings.tabs.authSettings.loginMethods.options.${authMethod.code}`,
                                    )}
                                  </MenuItem>
                                ))}
                              </TextField>
                            );
                          }}
                        </Field>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} lg={2}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <JumboTextField
                          select
                          InputLabelProps={{ required: false }}
                          name="customer.secondaryAuth"
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.authSettings.secondaryAuth.title",
                              )}
                            />
                          }
                        >
                          {DSOK_SECONDARY_AUTH_METHODS.map(
                            (authMethod, index) => {
                              return (
                                <MenuItem key={index} value={authMethod.code}>
                                  {t(
                                    `pages.dsokDetails.appSettings.tabs.authSettings.secondaryAuth.options.${authMethod.code}`,
                                  )}
                                </MenuItem>
                              );
                            },
                          )}
                        </JumboTextField>
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={"printer"} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.printer.printQrCode"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.printer.printQrCode",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printQrCode",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printQrCodeHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.printer?.printQrCode}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.printer.cutPaperBeforePrintOrderDetails"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.printer.cutPaperBeforePrintOrderDetails",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.printer.cutPaperBeforePrintOrderDetails",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.printer.cutPaperBeforePrintOrderDetailsHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={
                            values.settings?.printer
                              ?.cutPaperBeforePrintOrderDetails
                          }
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.printer.printDailyOrderCounter"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.printer.printDailyOrderCounter",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printDailyOrderCounter",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printDailyOrderCounterHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={
                            values.settings?.printer?.printDailyOrderCounter
                          }
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.printer.printOrderCode"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.printer.printOrderCode",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printOrderCode",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printOrderCodeHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.printer?.printOrderCode}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.printer.printPaymentReceipt"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.printer.printPaymentReceipt",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printPaymentReceipt",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printPaymentReceiptHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={
                            values.settings?.printer?.printPaymentReceipt
                          }
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.printer.printDiscountInfo"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.printer.printDiscountInfo",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printDiscountInfo",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printDiscountInfoHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={values.settings?.printer?.printDiscountInfo}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <Tooltip
                          arrow
                          title={t(
                            "pages.dsokDetails.appSettings.tabs.printer.printAuthenticatedUserHelp",
                          )}
                        >
                          <Box component="span">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    values.settings?.printer
                                      ?.printAuthenticatedUser !== false
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "settings.printer.printAuthenticatedUser",
                                      e.target.checked,
                                    )
                                  }
                                />
                              }
                              label={t(
                                "pages.dsokDetails.appSettings.tabs.printer.printAuthenticatedUser",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          </Box>
                        </Tooltip>
                      </Div>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={"accessSystem"} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.accessSystem.enabled"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.accessSystem.enabled",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.accessSystem.enabled",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.accessSystem.enabledHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={
                            values.settings?.accessSystem?.enabled === true
                          }
                        />
                      </Div>
                    </Grid>

                    {values.settings?.accessSystem?.enabled === true ? (
                      <Grid item xs={12} md={4} lg={4}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.accessSystem.printTickets"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.accessSystem.printTickets",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Label
                                text={t(
                                  "pages.dsokDetails.appSettings.tabs.accessSystem.printTickets",
                                )}
                                helpText={t(
                                  "pages.dsokDetails.appSettings.tabs.accessSystem.printTicketsHelp",
                                )}
                                sx={{ color: "text.primary" }}
                              />
                            }
                            checked={
                              values.settings?.accessSystem?.printTickets ===
                              true
                            }
                          />
                        </Div>
                      </Grid>
                    ) : null}

                    {values.settings?.accessSystem?.enabled === true &&
                      values.settings?.accessSystem?.printTickets === true && (
                        <Grid item xs={12} md={4} lg={4}>
                          <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Field
                                  name="settings.accessSystem.printQrCode"
                                  component={Switch}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "settings.accessSystem.printQrCode",
                                      e.target.checked,
                                    )
                                  }
                                />
                              }
                              label={
                                <Label
                                  text={t(
                                    "pages.dsokDetails.appSettings.tabs.accessSystem.printQrCode",
                                  )}
                                  helpText={t(
                                    "pages.dsokDetails.appSettings.tabs.accessSystem.printQrCodeHelp",
                                  )}
                                  sx={{ color: "text.primary" }}
                                />
                              }
                              checked={
                                values.settings?.accessSystem?.printQrCode ===
                                true
                              }
                            />
                          </Div>
                        </Grid>
                      )}
                    {values.settings?.accessSystem?.enabled === true &&
                      values.settings?.accessSystem?.printTickets === true && (
                        <>
                          <Grid item xs={12} md={4} lg={4}>
                            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Field
                                    name="settings.accessSystem.sendSmsEnabled"
                                    component={Switch}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "settings.accessSystem.sendSmsEnabled",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label={
                                  <Label
                                    text={t(
                                      "pages.dsokDetails.appSettings.tabs.accessSystem.sendSmsEnabled",
                                    )}
                                    helpText={t(
                                      "pages.dsokDetails.appSettings.tabs.accessSystem.sendSmsEnabledHelp",
                                    )}
                                    sx={{ color: "text.primary" }}
                                  />
                                }
                                checked={
                                  values.settings?.accessSystem
                                    ?.sendSmsEnabled === true
                                }
                              />
                            </Div>
                          </Grid>
                          <Grid item xs={12} md={4} lg={4}>
                            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Field
                                    name="settings.accessSystem.sendEmailEnabled"
                                    component={Switch}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "settings.accessSystem.sendEmailEnabled",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label={
                                  <Label
                                    text={t(
                                      "pages.dsokDetails.appSettings.tabs.accessSystem.sendEmailEnabled",
                                    )}
                                    helpText={t(
                                      "pages.dsokDetails.appSettings.tabs.accessSystem.sendEmailEnabledHelp",
                                    )}
                                    sx={{ color: "text.primary" }}
                                  />
                                }
                                checked={
                                  values.settings?.accessSystem
                                    ?.sendEmailEnabled === true
                                }
                              />
                            </Div>
                          </Grid>
                        </>
                      )}
                  </Grid>
                </TabPanel>
                <TabPanel value={"paymentMethods"} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    <Grid item xs={12} md={4} lg={4}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <Field name="customer.paymentMethods">
                          {({ field, meta }) => {
                            const value = ensureArrayForSelect(field.value);
                            return (
                              <TextField
                                select
                                fullWidth
                                InputLabelProps={{ required: false }}
                                {...field}
                                value={value}
                                onChange={(e) =>
                                  field.onChange({
                                    target: {
                                      name: field.name,
                                      value: e.target.value,
                                    },
                                  })
                                }
                                label={t(
                                  "pages.dsokDetails.appSettings.tabs.paymentMethods.title",
                                )}
                                SelectProps={{ multiple: true }}
                                error={!!meta.error}
                                helperText={meta.error}
                              >
                                {DSOK_PAYMENT_METHODS.map(
                                  (paymentMethod, index) => (
                                    <MenuItem
                                      key={index}
                                      value={paymentMethod.code}
                                    >
                                      {t(
                                        `pages.dsokDetails.appSettings.tabs.paymentMethods.options.${paymentMethod.code}`,
                                      )}
                                    </MenuItem>
                                  ),
                                )}
                              </TextField>
                            );
                          }}
                        </Field>
                      </FormControl>
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={"loyalty"} sx={{ p: 0, m: 0 }}>
                  <Grid item xs={12} md={12} lg={12} container spacing={2}>
                    <Grid item xs={12} md={4} lg={4}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name="settings.loyaltySystem.isEnabled"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "settings.loyaltySystem.isEnabled",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <Label
                              text={t(
                                "pages.dsokDetails.appSettings.tabs.loyalty.enabled",
                              )}
                              helpText={t(
                                "pages.dsokDetails.appSettings.tabs.loyalty.enabledHelp",
                              )}
                              sx={{ color: "text.primary" }}
                            />
                          }
                          checked={
                            values.settings?.loyaltySystem?.isEnabled === true
                          }
                        />
                      </Div>
                    </Grid>
                    {values.settings?.loyaltySystem?.isEnabled === true && (
                      <Grid item xs={12} md={4} lg={4}>
                        <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                          <JumboTextField
                            select
                            InputLabelProps={{ required: false }}
                            name="settings.loyaltySystem.type"
                            label={
                              <Label
                                text={t(
                                  "pages.dsokDetails.appSettings.tabs.loyalty.type",
                                )}
                              />
                            }
                          >
                            {DSOK_LOYALTY_SYSTEMS.map((system) => (
                              <MenuItem key={system.code} value={system.code}>
                                {t(
                                  `pages.dsokDetails.appSettings.tabs.loyalty.typeOptions.${system.code}`,
                                )}
                              </MenuItem>
                            ))}
                          </JumboTextField>
                        </FormControl>
                      </Grid>
                    )}
                    {values.settings?.loyaltySystem?.isEnabled === true &&
                      values.settings?.loyaltySystem?.type === "vexion" && (
                        <>
                          <Grid item xs={12} md={12} lg={12}></Grid>
                          <Grid item xs={12} md={3} lg={3}>
                            <FormControl
                              sx={{ mt: 1, mb: 1 }}
                              fullWidth
                              error={
                                !(
                                  values.settings?.loyaltySystem?.vexion
                                    ?.businessId ?? ""
                                ).trim() &&
                                values.settings?.loyaltySystem?.isEnabled &&
                                values.settings?.loyaltySystem?.type ===
                                  "vexion"
                              }
                            >
                              <JumboTextField
                                name="settings.loyaltySystem.vexion.businessId"
                                placeholder={t(
                                  "pages.dsokDetails.appSettings.tabs.loyalty.businessIdPlaceholder",
                                )}
                                label={
                                  <Label
                                    text={t(
                                      "pages.dsokDetails.appSettings.tabs.loyalty.businessId",
                                    )}
                                  />
                                }
                                helperText={
                                  !(
                                    values.settings?.loyaltySystem?.vexion
                                      ?.businessId ?? ""
                                  ).trim() &&
                                  values.settings?.loyaltySystem?.isEnabled &&
                                  values.settings?.loyaltySystem?.type ===
                                    "vexion"
                                    ? t(
                                        "pages.dsokDetails.appSettings.tabs.loyalty.businessIdRequired",
                                      )
                                    : undefined
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3} lg={3}>
                            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                              <FormControlLabel
                                control={
                                  <Field
                                    name="settings.loyaltySystem.vexion.enableRegistration"
                                    component={Switch}
                                    onChange={(e) =>
                                      setFieldValue(
                                        "settings.loyaltySystem.vexion.enableRegistration",
                                        e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label={
                                  <Label
                                    text={t(
                                      "pages.dsokDetails.appSettings.tabs.loyalty.enableRegistration",
                                    )}
                                    sx={{ color: "text.primary" }}
                                  />
                                }
                                checked={
                                  values.settings?.loyaltySystem?.vexion
                                    ?.enableRegistration === true
                                }
                              />
                            </Div>
                          </Grid>
                        </>
                      )}
                  </Grid>
                </TabPanel>
                {values?.customer?.lineOfBusiness === "concreteStation" && (
                  <TabPanel value={"concreteStation"} sx={{ p: 0, m: 0 }}>
                    <Grid item xs={12} md={12} lg={12} container spacing={2}>
                      <Grid item xs={12} md={8} lg={8}>
                        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Field
                                name="settings.concreteStation.startCompressorAfterOrder"
                                component={Switch}
                                onChange={(e) =>
                                  setFieldValue(
                                    "settings.concreteStation.startCompressorAfterOrder",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={
                              <Label
                                text={t(
                                  "pages.dsokDetails.appSettings.tabs.concreteStation.startCompressorAfterOrder",
                                  {
                                    defaultValue:
                                      "Spustiť kompresor po objednávke",
                                  },
                                )}
                                helpText={t(
                                  "pages.dsokDetails.appSettings.tabs.concreteStation.startCompressorAfterOrderHelp",
                                  {
                                    defaultValue:
                                      "Ak je označené, po vytvorení objednávky sa automaticky spustí kompresor. Komunikácia prebieha cez DLA. Na správne fungovanie musí byť kompresor nastavený v pracoviskách ako zariadenie so správnou IP adresou.",
                                  },
                                )}
                                sx={{ color: "text.primary" }}
                              />
                            }
                            checked={
                              values.settings?.concreteStation
                                ?.startCompressorAfterOrder === true
                            }
                          />
                        </Div>
                      </Grid>
                      {values.settings?.concreteStation
                        ?.startCompressorAfterOrder === true && (
                        <Grid item xs={12} md={8} lg={8}>
                          <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                            <CompressorDeviceSelector
                              customerId={values?.customer?.customerId}
                              values={values}
                              setFieldValue={setFieldValue}
                              t={t}
                            />
                          </Div>
                        </Grid>
                      )}
                    </Grid>
                  </TabPanel>
                )}
              </TabContext>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Modal
        open={openSelector !== undefined}
        onClose={() => setOpenSelector()}
      >
        <Div sx={modalStyle}>
          <Galleries
            selectorMode
            handleSelect={(image) => handleGallerySelect(image, openSelector)}
          />
        </Div>
      </Modal>
    </>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.appSettings.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default SmartkioskSettingsFields;
